import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const filteredComics = slug === 'all'
    ? comics
    : comics.filter((c) => c.categorySlug === slug);

  const displayName = slug === 'all' ? '全部漫画' : (category?.name || slug);

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: '分类', path: '/category/all' },
    { label: displayName, path: `/category/${slug}` },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} - 073漫画网`,
    description: `${displayName}漫画在线免费阅读，下拉式流畅体验，尽在073漫画网。`,
    url: `http://dqcmwfc.com/category/${slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((bc, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: { '@id': `http://dqcmwfc.com/${bc.path}`, name: bc.label },
      })),
    },
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />

        <nav className="max-w-[1280px] mx-auto px-6 md:px-10 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            {breadcrumbs.map((bc, i) => (
              <li key={bc.path} className="flex items-center gap-2">
                {i > 0 && <span className="text-foreground-300">/</span>}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={bc.path} className="hover:text-primary-600 transition-colors">
                    {bc.label}
                  </Link>
                ) : (
                  <span className="text-foreground-700 font-medium">{bc.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-8">
          <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-2">
            {displayName}
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            共 {filteredComics.length} 部漫画
          </p>

          {/* Category Switcher Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 pb-2">
            <Link
              to="/category/all"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                slug === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200'
              }`}
            >
              全部
              <span className="ml-1 text-xs opacity-70">{comics.length}</span>
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200'
                }`}
              >
                {cat.name}
                <span className="ml-1 text-xs opacity-70">{cat.count}</span>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500">暂无该分类的漫画</p>
              <Link to="/category/all" className="text-primary-600 hover:text-primary-700 text-sm mt-2 inline-block">
                浏览全部漫画
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group flex flex-col cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 在线免费阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                      {comic.status}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-accent-600 flex items-center gap-0.5">
                      <i className="ri-star-fill w-3 h-3 flex items-center justify-center"></i>
                      {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-500">{comic.author}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}